import { Injectable } from '@angular/core';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

export interface GeocodedLocation {
  lat: number;
  lng: number;
  address: string;
  cachedAt: number;
}

export interface GeocodingResult {
  success: boolean;
  lat?: number;
  lng?: number;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GeocodingService {
  private readonly CACHE_KEY = 'geocode_cache';
  private readonly CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  private geocoder: google.maps.Geocoder | null = null;

  constructor(private mapsLoader: GoogleMapsLoaderService) {}

  private getGeocoder(): google.maps.Geocoder {
    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }
    return this.geocoder;
  }

  private getCache(): Map<string, GeocodedLocation> {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        return new Map(JSON.parse(cached));
      }
    } catch (e) {
      console.warn('Failed to load geocode cache:', e);
    }
    return new Map();
  }

  private saveCache(cache: Map<string, GeocodedLocation>): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify([...cache]));
    } catch (e) {
      console.warn('Failed to save geocode cache:', e);
    }
  }

  private normalizeAddress(address: string, city: string, postalCode: string, country: string): string {
    return [address, postalCode, city, country]
      .filter(part => part && part.trim())
      .join(', ')
      .toLowerCase()
      .trim();
  }

  async geocodeAddress(
    address: string,
    city: string,
    postalCode: string,
    country: string
  ): Promise<GeocodingResult> {
    const normalizedAddress = this.normalizeAddress(address, city, postalCode, country);

    if (!normalizedAddress) {
      return { success: false, error: 'No address provided' };
    }

    // Check cache first
    const cache = this.getCache();
    const cached = cache.get(normalizedAddress);

    if (cached && (Date.now() - cached.cachedAt) < this.CACHE_TTL) {
      return { success: true, lat: cached.lat, lng: cached.lng };
    }

    // Ensure Google Maps is loaded before geocoding
    try {
      await this.mapsLoader.load();
    } catch (error) {
      return { success: false, error: 'Google Maps API not available' };
    }

    // Geocode the address
    try {
      const geocoder = this.getGeocoder();
      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: normalizedAddress }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      const location = result[0].geometry.location;
      const lat = location.lat();
      const lng = location.lng();

      // Save to cache
      cache.set(normalizedAddress, {
        lat,
        lng,
        address: normalizedAddress,
        cachedAt: Date.now()
      });
      this.saveCache(cache);

      return { success: true, lat, lng };
    } catch (error) {
      console.warn(`Failed to geocode: ${normalizedAddress}`, error);
      return { success: false, error: String(error) };
    }
  }

  clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
  }
}
